"use client";
import {useState} from "react";
import {
  Box,
  Center,
  Divider,
  Flex,
  Heading,
  SimpleGrid,
  Stack,
  VStack,
  Text,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import PricingFooter from "@/components/pricingFooter";
import {useSectionHeadingVariant} from "@/theme/components/useSectionHeading";
import {PricingGroup, ServerGroup, NodeCountInput} from "../elements";
import {KUBERNETES_DATA, SECTION_CONSTANTS} from "./constants";

const getCalculatedPrice = (price, nodeCount, frequency) => {
  if (frequency === "monthly") {
    return (price * nodeCount).toFixed(2);
  } else {
    return ((price / 730) * nodeCount).toFixed(2);
  }
};

const DesktopView = ({data, nodeCount, frequency}) => {
  return (
    <Box>
      <TableContainer>
        <Table variant="striped" border="2px solid rgb(237,242,247)" width="100%">
          <Thead>
            <Tr>
              <Th textAlign="center" textTransform="none">
                vCPUs
              </Th>
              <Th textAlign="center">Nodes </Th>
              <Th textAlign="center">Memory</Th>
              <Th textAlign="center">Storage</Th>
              <Th textAlign="center">
                Pricing ({frequency === "monthly" ? "Monthly" : "Hourly"})
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {data.map((item, index) => (
              <Tr key={index}>
                <Td textAlign="center">{item.cpu}</Td>
                <Td textAlign="center">{nodeCount}</Td>
                <Td textAlign="center">{item.memory}</Td>
                <Td textAlign="center">{item.storage}</Td>
                <Td textAlign="center" fontWeight="700" fontSize="18px" color="blue.600">
                  ${getCalculatedPrice(item.monthlyPrice, nodeCount, frequency)}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const MobileView = ({data, nodeCount, frequency}) => {
  return (
    <SimpleGrid columns={2} spacing={6}>
      {data.map((plan, index) => (
        <VStack
          key={index}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow="md"
          spacing={4}
          w="100%"
          p={4}
          bg="white"
        >
          <Flex direction="column" w="full">
            <Text fontWeight="bold" fontSize="lg">
              {plan.cpu} vCPUs
            </Text>
          </Flex>

          <Divider borderColor="gray.200" />

          <Stack spacing={2} w="full">
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Memory:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.memory}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Storage:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.storage}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Nodes:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {nodeCount}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Pricing ({frequency === "monthly" ? "Monthly" : "Hourly"}):{" "}
              </Text>
              <Text as="span" fontWeight="bold" fontSize="lg" color="blue.600">
                ${getCalculatedPrice(plan.monthlyPrice, nodeCount, frequency)}
              </Text>
            </Box>
          </Stack>
        </VStack>
      ))}
    </SimpleGrid>
  );
};

const KubernetesTable = () => {
  const [serverType, setServerType] = useState("intel");
  const [frequency, setFrequency] = useState("monthly");
  const [nodeCount, setNodeCount] = useState(3);

  const data =
    serverType === "intel" ? KUBERNETES_DATA.intelData : KUBERNETES_DATA.amData;
  const {heading, footer} = SECTION_CONSTANTS;
  const HeadingVariant = useSectionHeadingVariant();

  return (
    <Box id="kubernetes">
      <Heading variant={HeadingVariant} textAlign="center" mb={4}>
        {heading}
      </Heading>

      <ServerGroup server={serverType} onChange={setServerType} />

      <Flex justify="space-between" flexWrap="wrap" gap={{base: 4, md: 0}}>
        <NodeCountInput nodeCount={nodeCount} onChange={setNodeCount} />
        <PricingGroup frequency={frequency} onChange={setFrequency} />
      </Flex>

      <Center display={{md: "none"}} w="full">
        <MobileView data={data} nodeCount={nodeCount} frequency={frequency} />
      </Center>

      <Box display={{base: "none", md: "block"}}>
        <DesktopView data={data} nodeCount={nodeCount} frequency={frequency} />
      </Box>

      <PricingFooter {...footer} />
      <Divider borderWidth="2px" borderColor="#ECECEC" my={10} />
    </Box>
  );
};

export default KubernetesTable;
